// 배당금 > 연간 — 금융소득 종합과세 자동 계산 카드
// 배당(국내 원화 + 해외 수령일 환율 환산) + 사용자가 입력한 이자소득 → 연 2,000만원 기준 추적,
// 초과 시 다른 종합소득 과세표준을 반영해 추가 납부세액(지방세 포함) 추정.
import { useState, useEffect } from "react";
import { financialIncomeByYear, FIN_INCOME_THRESHOLD } from "./lib/dividends";
import { Ico } from "./lib/icons";

const mono = { fontFamily: "'DM Mono', monospace" };
const won = (n) => `₩${Math.round(n || 0).toLocaleString("ko-KR")}`;
const man = (n) => {
  const v = Math.round(n || 0);
  if (Math.abs(v) >= 100000000) return `${(v / 100000000).toFixed(2).replace(/\.?0+$/, "")}억원`;
  return `${Math.round(v / 10000).toLocaleString("ko-KR")}만원`;
};
const digits = (s) => String(s || "").replace(/[^\d]/g, "");
const withComma = (s) => { const d = digits(s); return d ? Number(d).toLocaleString("ko-KR") : ""; };

function MoneyInput({ label, hint, value, onSave }) {
  const [text, setText] = useState(withComma(value));
  useEffect(() => { setText(withComma(value)); }, [value]);
  const commit = () => { const d = digits(text); if (d !== digits(value)) onSave(d === "" ? "" : Number(d)); };
  return (
    <label style={{ display: "block", flex: "1 1 200px" }}>
      <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 5 }}>{label}</div>
      <div style={{ position: "relative" }}>
        <input value={text} inputMode="numeric" placeholder="0"
          onChange={e => setText(withComma(e.target.value))}
          onBlur={commit} onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 30px 8px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", fontSize: 13, textAlign: "right", ...mono, outline: "none" }} />
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--text-sub)" }}>원</span>
      </div>
      {hint && <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>{hint}</div>}
    </label>
  );
}

function Row({ label, value, color, bold, sub }) {
  return (
    <>
      <span style={{ color: "var(--text-sub)", fontSize: sub ? 11 : 12, paddingLeft: sub ? 10 : 0 }}>{label}</span>
      <span style={{ textAlign: "right", color: color || "var(--text)", fontWeight: bold ? 700 : 500, fontSize: sub ? 12 : 13, ...mono }}>{value}</span>
    </>
  );
}

const KIND = {
  normal: { label: "일반 과세", color: "#9ca3af" },
  partial: { label: "부분과세", color: "#fbbf24" },
  taxbase: { label: "과세표준", color: "#60a5fa" },
  exempt: { label: "비과세 계좌", color: "#34d399" },
  "mixed-exempt": { label: "일부 비과세", color: "#34d399" },
  mixed: { label: "혼합", color: "#a78bfa" },
};

function StockBreakdown({ y }) {
  const [open, setOpen] = useState(false);
  const [showExempt, setShowExempt] = useState(false);
  const rows = (y.stocks || []).filter(st => showExempt || st.kind !== "exempt");
  const exemptOnly = (y.stocks || []).filter(st => st.kind === "exempt").length;
  const th = { padding: "8px 10px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, textAlign: "right", whiteSpace: "nowrap", borderBottom: "1px solid var(--border)" };
  const td = { padding: "8px 10px", fontSize: 12, textAlign: "right", whiteSpace: "nowrap", borderBottom: "1px solid var(--border)", ...mono };
  const totals = rows.reduce((a, st) => ({ gross: a.gross + st.grossKrw, taxable: a.taxable + st.taxableKrw, out: a.out + st.exemptKrw + st.reducedKrw, withheld: a.withheld + st.withheldKrw }), { gross: 0, taxable: 0, out: 0, withheld: 0 });
  return (
    <div style={{ marginTop: 18 }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
        border: "1px solid rgba(251,191,36,0.3)", background: open ? "rgba(251,191,36,0.08)" : "transparent", color: "var(--text)",
      }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>종목별 상세보기 <span style={{ fontSize: 11, color: "var(--text-sub)", fontWeight: 500 }}>· {(y.stocks || []).length}종목 · 배당 {y.count}건</span></span>
        <span style={{ fontSize: 12, color: "#fbbf24" }}>{open ? "접기 ▲" : "펼치기 ▼"}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {(y.stocks || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-sub)", padding: 12 }}>{y.year}년 배당 기록이 없어요.</div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: "left" }}>종목</th>
                      <th style={{ ...th, textAlign: "left" }}>과세 유형</th>
                      <th style={th}>횟수</th>
                      <th style={th}>받은 배당 (세전·원화)</th>
                      <th style={th}>금융소득 반영</th>
                      <th style={th}>빠진 금액</th>
                      <th style={th}>원천징수</th>
                      <th style={{ ...th, width: 120 }}>비중</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(st => {
                      const k = KIND[st.kind] || KIND.normal;
                      const out = st.exemptKrw + st.reducedKrw;
                      return (
                        <tr key={st.name + st.currency}>
                          <td style={{ ...td, textAlign: "left", fontFamily: "inherit", fontWeight: 600 }}>
                            {st.name}
                            <span style={{ marginLeft: 6, fontSize: 10, color: "var(--text-muted)", fontWeight: 500 }}>{st.currency === "USD" ? `$${st.grossOrig.toLocaleString()}` : ""}</span>
                          </td>
                          <td style={{ ...td, textAlign: "left", fontFamily: "inherit" }}>
                            <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, border: `1px solid ${k.color}55`, color: k.color }}>{k.label}</span>
                          </td>
                          <td style={td}>{st.count}회</td>
                          <td style={td}>{won(st.grossKrw)}</td>
                          <td style={{ ...td, fontWeight: 700, color: st.taxableKrw > 0 ? "#fbbf24" : "var(--text-muted)" }}>{won(st.taxableKrw)}</td>
                          <td style={{ ...td, color: out > 0 ? "#34d399" : "var(--text-muted)" }} title={st.exemptKrw > 0 ? `비과세 계좌 ${won(st.exemptKrw)}` : st.reducedKrw > 0 ? "부분과세·과세표준으로 금융소득에서 빠진 몫" : ""}>{out > 0 ? `−${won(out)}` : "—"}</td>
                          <td style={td}>{st.withheldKrw > 0 ? won(st.withheldKrw) : "—"}</td>
                          <td style={td}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                              <div style={{ width: 60, height: 6, borderRadius: 3, background: "var(--bg-hover)", overflow: "hidden" }}>
                                <div style={{ width: `${Math.round(st.share * 100)}%`, height: "100%", background: "#fbbf24" }} />
                              </div>
                              <span style={{ fontSize: 11, minWidth: 34 }}>{(st.share * 100).toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td style={{ ...td, textAlign: "left", fontFamily: "inherit", fontWeight: 700 }} colSpan={3}>합계{showExempt ? "" : " (비과세 계좌만 있는 종목 제외)"}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{won(totals.gross)}</td>
                      <td style={{ ...td, fontWeight: 800, color: "#fbbf24" }}>{won(totals.taxable)}</td>
                      <td style={{ ...td, fontWeight: 700, color: "#34d399" }}>{totals.out > 0 ? `−${won(totals.out)}` : "—"}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{won(totals.withheld)}</td>
                      <td style={td} />
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                <div style={{ fontSize: 10, color: "var(--text-muted)", lineHeight: 1.7 }}>
                  금융소득 반영 = 일반 과세는 세전 전액, 부분과세는 세금÷15%, 과세표준 입력 종목은 과세표준×주수. 해외는 수령일 환율로 원화 환산. 비중은 배당 금융소득 합계 대비.
                </div>
                {exemptOnly > 0 && (
                  <button type="button" onClick={() => setShowExempt(v => !v)} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", border: "1px solid var(--border)", background: "transparent", color: "var(--text-sub)" }}>
                    {showExempt ? "비과세 계좌 종목 숨기기" : `비과세 계좌 종목 ${exemptOnly}개 보기`}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function FinancialIncomeTax({ dividends, rate, settings, onSave }) {
  const thisYear = String(new Date(Date.now() + 9 * 3600 * 1000).getUTCFullYear());
  const years = financialIncomeByYear(dividends, rate, settings, [thisYear]);
  const [openYear, setOpenYear] = useState(thisYear);

  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 16, padding: 20, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#fbbf24" }}>금융소득 종합과세</div>
        <div style={{ fontSize: 11, color: "var(--text-sub)" }}>기준 연 {man(FIN_INCOME_THRESHOLD)} · 국내+해외 배당(원화 환산) + 이자 · 비과세 계좌 제외</div>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0 16px" }}>
        {years.map(y => (
          <button key={y.year} onClick={() => setOpenYear(y.year)} style={{
            padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
            border: `1px solid ${openYear === y.year ? "rgba(251,191,36,0.6)" : "var(--border)"}`,
            background: openYear === y.year ? "rgba(251,191,36,0.12)" : "transparent",
            color: openYear === y.year ? "#fbbf24" : "var(--text-sub)",
          }}>
            {y.year}{y.comprehensive && <span style={{ color: "#f87171", marginLeft: 4 }}>●</span>}
          </button>
        ))}
      </div>

      {years.filter(y => y.year === openYear).map(y => {
        const pct = Math.round(y.progress * 100);
        const barColor = y.comprehensive ? "#f87171" : pct >= 80 ? "#fbbf24" : "#34d399";
        return (
          <div key={y.year}>
            {/* 진행률 */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div style={{ fontSize: 13, color: "var(--text)" }}>
                {y.year}년 금융소득 <b style={{ fontSize: 20, color: barColor, ...mono }}>{won(y.total)}</b>
              </div>
              <div style={{ fontSize: 12, color: barColor, fontWeight: 700 }}>
                {y.comprehensive ? `기준 ${man(y.over)} 초과 → 종합과세 대상` : `기준까지 ${man(y.remaining)} 남음 (${pct}%)`}
              </div>
            </div>
            <div style={{ height: 10, borderRadius: 5, background: "var(--bg-hover)", overflow: "hidden", marginBottom: 18 }}>
              <div style={{ width: `${Math.max(2, pct)}%`, height: "100%", background: barColor, borderRadius: 5, transition: "width .3s" }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
              {/* 구성 */}
              <div>
                <div style={{ fontSize: 10, color: "var(--text-sub)", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>금융소득 구성</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "7px 16px" }}>
                  <Row label="국내 배당 (원화)" value={won(y.domesticKrw)} sub />
                  <Row label="해외 배당 (수령일 환율 환산)" value={won(y.foreignKrw)} sub />
                  <Row label="이자소득 (직접 입력)" value={won(y.interest)} sub />
                  <Row label="합계" value={won(y.total)} bold color={barColor} />
                  {y.exemptKrw > 0 && <Row label="※ 제외: 비과세 계좌 배당" value={won(y.exemptKrw)} sub color="var(--text-muted)" />}
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
                  <MoneyInput label="이자소득 (연간, 세전)" hint="예금·채권·CMA 이자 등" value={settings?.[y.year]?.interest}
                    onSave={v => onSave(y.year, { interest: v })} />
                  <MoneyInput label="다른 종합소득 과세표준" hint="근로·사업소득에서 공제 뺀 금액 (연말정산 원천징수영수증)" value={settings?.[y.year]?.otherBase}
                    onSave={v => onSave(y.year, { otherBase: v })} />
                </div>
              </div>

              {/* 세금 */}
              <div style={{ padding: 16, borderRadius: 12, background: y.comprehensive ? "rgba(248,113,113,0.06)" : "rgba(52,211,153,0.06)", border: `1px solid ${y.comprehensive ? "rgba(248,113,113,0.25)" : "rgba(52,211,153,0.25)"}` }}>
                {!y.comprehensive ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#34d399", marginBottom: 8 }}>분리과세로 끝 — 추가 세금 없음</div>
                    <div style={{ fontSize: 12, color: "var(--text-sub)", lineHeight: 1.7 }}>
                      2,000만원 이하라 배당·이자 받을 때 떼인 15.4%(지방세 포함)로 납세가 끝나요.<br />
                      원천징수 추정 {won(y.withheld * 1.1)} (지방세 포함)
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>5월 종합소득세 신고 때 추가 납부 예상 (지방세 포함)</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: "#f87171", ...mono, marginBottom: 12 }}>{won(y.extraWithLocal)}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px 14px" }}>
                      <Row label="종합과세 되는 금액 (2,000만원 초과분)" value={won(y.over)} sub />
                      <Row label="적용 최고세율 (국세)" value={`${Math.round(y.topRate * 100)}%`} sub />
                      <Row label="① 일반산출세액" value={won(y.generalTax)} sub />
                      <Row label="② 비교산출세액 (전체×14% + 다른소득)" value={won(y.compareTax)} sub />
                      <Row label="추가 국세 = ① − ② (0 이상)" value={won(y.extra)} sub />
                    </div>
                    {!y.hasOtherBase && (
                      <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 10 }}><Ico tint={false} n="triangle-alert" />다른 종합소득 과세표준을 입력하지 않아 0원으로 계산했어요. 근로소득이 있으면 실제 추가 세금이 더 커요.</div>
                    )}
                  </>
                )}
              </div>
            </div>
            <StockBreakdown y={y} />
          </div>
        );
      })}

      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 16, lineHeight: 1.7 }}>
        추정치입니다. 비교과세(일반산출세액 vs 전체 14%) 기준이며, Gross-up·배당세액공제, 외국납부세액공제 한도, 고배당기업 배당 분리과세(2026~2028),
        건강보험료 변동은 반영하지 않았어요. 기본감세 종목은 주당과세표준×주수, 비과세 체크 배당은 제외합니다. 실제 신고는 홈택스·세무사와 확인하세요.
      </div>
    </div>
  );
}
